<template>
  <div class="musubi-page dm-page">
    <header class="musubi-page__head">
      <div class="dm-head-row">
        <h1>Direct Mail - Buy this campaign <span class="dm-badge">New</span></h1>
        <span class="plan-pill plan-pill--free"><CygnusIcon name="info-circle" /> Free plan</span>
      </div>
      <p class="text-muted">A one-off direct mail purchase - no plan needed. You pay for print, postage and a per-record data fee, billed once through <strong>Hermes</strong>. Nothing prints until a human confirms your proof and dispatch by email.</p>
    </header>

    <!-- Stepper -->
    <nav class="dm-steps" aria-label="Direct mail steps">
      <router-link :to="audienceLink" class="dm-step is-done">
        <span class="dm-step__num"><CygnusIcon name="check" /></span>
        <span class="dm-step__body"><span class="dm-step__title">Who gets mail</span><span class="dm-step__sub">Audience</span></span>
      </router-link>
      <router-link :to="designLink" class="dm-step is-done">
        <span class="dm-step__num"><CygnusIcon name="check" /></span>
        <span class="dm-step__body"><span class="dm-step__title">Design your mail piece</span><span class="dm-step__sub">{{ formatLabel }}</span></span>
      </router-link>
      <div class="dm-step is-current">
        <span class="dm-step__num">3</span>
        <span class="dm-step__body"><span class="dm-step__title">Buy this campaign</span><span class="dm-step__sub">One-off checkout</span></span>
      </div>
    </nav>

    <div v-if="error" class="dm-alert dm-alert--err" style="margin-bottom:16px">{{ error }}</div>
    <div v-if="cancelled" class="dm-alert dm-alert--warn" style="margin-bottom:16px">Payment was cancelled. Your mailing is saved - you can buy it whenever you are ready.</div>
    <div v-if="loading" class="text-muted">{{ confirming ? 'Confirming your payment…' : 'Preparing your checkout…' }}</div>

    <template v-if="loaded">
      <!-- ── Batch summary ── -->
      <div class="sum-grid" style="margin-bottom:18px">
        <div class="sum-card">
          <div class="sum-card__lbl"><CygnusIcon name="users" />Batch</div>
          <div class="sum-card__big">{{ fmt(batch.recipient_count) }}</div>
          <div class="sum-card__sub">recipients - full list, unlocked by this purchase</div>
          <ul class="sum-list">
            <li><span>Excluded - incomplete address</span><b>{{ fmt(batch.excluded_incomplete) }}</b></li>
            <li><span>Suppressed - do not mail</span><b>{{ fmt(batch.suppressed_count) }}</b></li>
            <li><span>Skipped - mailed in the last 30 days</span><b>{{ fmt(batch.capped_count) }}</b></li>
          </ul>
        </div>
        <div class="sum-card">
          <div class="sum-card__lbl"><CygnusIcon name="file" />Format</div>
          <div class="sum-card__big">{{ formatLabel }}</div>
          <div class="sum-card__sub">{{ formatDims }}</div>
          <ul class="sum-list">
            <li><span>Quantity</span><b>{{ fmt(batch.recipient_count) }} x {{ formatLabel }}</b></li>
            <li><span>Personalisation</span><b>Address block</b></li>
            <li><span>Postage</span><b>2nd class, batch</b></li>
          </ul>
        </div>
        <div class="sum-card">
          <div class="sum-card__lbl"><CygnusIcon name="file" />Proof</div>
          <div class="proof-thumb">
            <div class="proof-thumb__vis"><CygnusIcon name="file" /></div>
            <button v-if="isBuilderSource" type="button" class="btn btn-sec btn-sm" @click="openProof"><CygnusIcon name="file" /> Open proof</button>
            <span class="proof-thumb__note">{{ isBuilderSource ? 'sample record merged' : 'Prints as your supplied PDF, with the address block overlaid.' }}</span>
          </div>
        </div>
      </div>

      <!-- ── Itemised pricing ── -->
      <section class="dm-card">
        <div class="dm-card__head"><CygnusIcon name="coins" class="dm-card__icon" /><h3 class="dm-card__title">What this costs</h3><span class="dm-card__subtitle">{{ fmt(quote.quantity) }} x {{ formatLabel }} - one-off, pay-as-you-go</span></div>
        <div class="dm-card__body">
          <table class="cost">
            <thead><tr><th>Line</th><th>Basis</th><th class="num">Unit</th><th class="num">Amount</th></tr></thead>
            <tbody>
              <tr v-for="line in quote.lines" :key="line.key" :class="{ 'cost-row--data': line.key === 'data_fee' }">
                <td>
                  {{ line.label }}
                  <span v-if="line.key === 'data_fee'" class="data-tag"><CygnusIcon name="lock" /> pay-as-you-go</span>
                </td>
                <td>{{ line.basis }}</td>
                <td class="num">{{ pence(line.unit_pence) }}p</td>
                <td class="num">{{ money(line.amount_pence) }}</td>
              </tr>
            </tbody>
            <tfoot>
              <tr><td colspan="3">Subtotal (ex VAT)</td><td class="num">{{ money(quote.subtotal_pence) }}</td></tr>
              <tr><td colspan="3">VAT ({{ quote.vat_percent }}%)</td><td class="num">{{ money(quote.vat_pence) }}</td></tr>
              <tr class="grand"><td colspan="3">{{ quote.vat_estimated ? 'Total (estimated)' : 'Total' }}</td><td class="num">{{ money(quote.total_pence) }}</td></tr>
            </tfoot>
          </table>
          <div v-if="quote.vat_estimated" class="cost-note"><CygnusIcon name="info-circle" />VAT and the total are estimated, confirmed at payment.</div>
          <div class="cost-note"><CygnusIcon name="info-circle" />The data fee applies to pay-as-you-go batches only. It covers one-off use of the shared corpus for this send - it is waived on every paid plan. Prices shown are placeholders from our current rate card.</div>
        </div>
      </section>

      <!-- ── Savings / plan comparison ── -->
      <div v-if="quote.savings_on_plan_pence > 0" class="save-card">
        <div class="save-card__ic"><CygnusIcon name="rocket" /></div>
        <div class="save-card__body">
          <div class="save-card__k">On a paid plan</div>
          <div class="save-card__h">This batch would cost <b>{{ money(quote.savings_on_plan_pence) }} less</b> - the data fee is waived</div>
          <p class="save-card__p">Paid plans drop the per-record data fee entirely, and unlock the closed loop that turns a postcard into tracked pipeline.</p>
        </div>
        <div class="save-card__act"><router-link class="btn btn-violet" to="/admin/usage-plans"><CygnusIcon name="rocket" /> See plans</router-link></div>
      </div>

      <!-- ── Payment: Hermes handoff ── -->
      <section class="dm-card">
        <div class="dm-card__head"><CygnusIcon name="credit-card" class="dm-card__icon" /><h3 class="dm-card__title">Payment</h3><span class="dm-card__subtitle">secure checkout, handled by Hermes</span></div>
        <div class="dm-card__body">
          <div class="pay-panel">
            <div class="hermes-box">
              <div class="hermes-wm"><span class="hermes-wm__mark"><CygnusIcon name="shield" /></span> Hermes <span class="tag">Billing</span></div>
              <p class="hermes-box__note">Card details are entered on Hermes' secure payment page, not here. We never see or store your card. You will be brought back to Yorlin once payment is confirmed.</p>
              <div class="hermes-box__row"><CygnusIcon name="check" /> PCI-compliant card capture</div>
              <div class="hermes-box__row"><CygnusIcon name="check" /> One-off charge - no subscription, no auto-renew</div>
              <div class="hermes-box__row"><CygnusIcon name="check" /> VAT receipt emailed on payment</div>
            </div>

            <div class="order-sum">
              <div class="order-sum__h">Order</div>
              <div v-for="line in quote.lines" :key="line.key" class="order-sum__line"><span>{{ shortLabel(line) }}</span><b>{{ money(line.amount_pence) }}</b></div>
              <div class="order-sum__line"><span>VAT ({{ quote.vat_percent }}%)</span><b>{{ money(quote.vat_pence) }}</b></div>
              <div class="order-sum__total"><span>{{ quote.vat_estimated ? 'Total (estimated, confirmed at payment)' : 'Total' }}</span><span class="num">{{ money(quote.total_pence) }}</span></div>
            </div>
          </div>

          <div class="dm-action-bar" style="margin-top:16px">
            <p class="pay-terms">
              <CygnusIcon name="info-circle" />
              Placing this order authorises payment for print, postage and the data fee. A member of our team confirms your <b>proof and dispatch date by email before anything is printed</b> - if the proof is not right, we hold and refund.
            </p>
            <span class="dm-action-bar__spacer"></span>
            <router-link :to="designLink" class="btn btn-sec"><CygnusIcon name="arrow-left" /> Back</router-link>
            <button class="btn btn-pri btn-lg" :disabled="paying" @click="continueToPayment">
              <CygnusIcon name="shield" /> {{ paying ? 'Redirecting…' : 'Continue to secure payment' }}
            </button>
          </div>
        </div>
      </section>
    </template>
  </div>
</template>

<script>
import useApi from "@apexl/icarus-core/src/services/api";

/**
 * DirectMailCheckout — "Buy this campaign" (Direct Mail slice DM-4).
 *
 * Faithful to mockups/direct-mail/checkout.html: the batch + format + proof
 * summary, the itemised one-off pricing table (with the violet data-fee row), the
 * savings-on-plan nudge, and the Hermes-hosted payment handoff. Card fields are
 * NEVER rendered here - "Continue to secure payment" creates a Hermes checkout
 * and redirects the shopper to Hermes' hosted page; on return we confirm the
 * payment and, on success, land on the review page's status timeline.
 *
 * A PAYING tenant that reaches this page is bounced straight to the plain review
 * (their plan already covers the data). Copy uses plain hyphens (never em dashes).
 */
export default {
  name: "DirectMailCheckout",
  props: { componentData: { type: Object, default: () => ({}) } },
  data() {
    return {
      api: useApi(),
      loading: true,
      loaded: false,
      confirming: false,
      paying: false,
      cancelled: false,
      error: null,
      batchId: 0,
      designId: 0,
      audienceId: 0,
      batch: { id: 0, format: "postcard_a6", recipient_count: 0, excluded_incomplete: 0, suppressed_count: 0, capped_count: 0 },
      quote: { quantity: 0, lines: [], subtotal_pence: 0, vat_pence: 0, vat_percent: 20, total_pence: 0, savings_on_plan_pence: 0 },
      design: null,
      audience: null,
    };
  },
  computed: {
    formatLabel() {
      return this.quote.format_label || this.batch.format;
    },
    formatDims() {
      return this.design && this.design.dims_label ? this.design.dims_label : "";
    },
    isBuilderSource() {
      return !this.design || (this.design.source || "builder") === "builder";
    },
    reviewLink() {
      const query = {};
      if (this.batchId) query.batch = this.batchId;
      if (this.designId) query.design = this.designId;
      if (this.audienceId) query.audience = this.audienceId;
      return { path: "/outreach/direct-mail/review", query };
    },
    designLink() {
      const query = {};
      if (this.audienceId) query.audience = this.audienceId;
      return { path: "/outreach/direct-mail/design", query };
    },
    audienceLink() {
      return this.audienceId ? { path: "/outreach/direct-mail", query: { audience: this.audienceId } } : { path: "/outreach/direct-mail" };
    },
  },
  created() {
    const q = this.$route && this.$route.query ? this.$route.query : {};
    this.batchId = Number(q.batch) || 0;
    this.designId = Number(q.design) || 0;
    this.audienceId = Number(q.audience) || 0;
    this.cancelled = String(q.cancelled || "") === "1";
    this.boot(String(q.return || "") === "1");
  },
  methods: {
    async boot(isReturn) {
      if (!this.batchId) {
        this.error = "No mailing selected. Start from your Direct Mail audience.";
        this.loading = false;
        return;
      }
      // Returning from Hermes: confirm payment first.
      if (isReturn) {
        await this.confirmPayment();
        return;
      }
      await this.loadAll();
    },
    async loadAll() {
      try {
        // Pricing (batch + quote) and the design/audience meta (proof) in parallel.
        const [quoteRes, metaRes] = await Promise.all([
          this.api.get("/api/v1/direct-mail/batches/" + this.batchId + "/quote"),
          this.api.get("/api/v1/direct-mail/batches/" + this.batchId),
        ]);
        const qBody = quoteRes?.data ?? quoteRes;
        if (qBody?.batch) this.batch = { ...this.batch, ...qBody.batch };
        if (qBody?.quote) this.quote = { ...this.quote, ...qBody.quote };
        const mBody = metaRes?.data ?? metaRes;
        if (mBody?.design) this.design = mBody.design;
        if (mBody?.audience) this.audience = mBody.audience;
        if (this.batch.design_id) this.designId = this.batch.design_id;
        if (this.batch.audience_id) this.audienceId = this.batch.audience_id;

        // A paying tenant's plan covers the data - send them to the plain review.
        if (this.quote.has_active_paid_plan) {
          this.$router.replace(this.reviewLink);
          return;
        }
        // Already submitted / paid - nothing to buy; show the timeline on review.
        if (this.batch.status && this.batch.status !== "draft") {
          this.$router.replace(this.reviewLink);
          return;
        }
        this.loaded = true;
      } catch (e) {
        this.error = this.errText(e, "Could not load your checkout. Please try again.");
      } finally {
        this.loading = false;
      }
    },
    async continueToPayment() {
      if (!this.batchId || this.paying) return;
      this.paying = true;
      try {
        const res = await this.api.post("/api/v1/direct-mail/batches/" + this.batchId + "/checkout", {});
        const body = res?.data ?? res;
        if (body?.plan_covered || body?.already_submitted) {
          this.$router.replace(this.reviewLink);
          return;
        }
        if (body?.checkout_url) {
          // Hand off to the Hermes-hosted payment page.
          window.location.assign(body.checkout_url);
          return;
        }
        this.error = "We could not start the payment step. Please try again shortly.";
        this.paying = false;
      } catch (e) {
        this.error = this.errText(e, "We could not start the payment step. Please try again shortly.");
        this.paying = false;
      }
    },
    async confirmPayment() {
      this.confirming = true;
      this.loading = true;
      try {
        const res = await this.api.post("/api/v1/direct-mail/batches/" + this.batchId + "/checkout/confirm", {});
        const body = res?.data ?? res;
        const status = body?.status || "pending";
        if (body?.batch) this.batch = { ...this.batch, ...body.batch };
        if (this.batch.audience_id) this.audienceId = this.batch.audience_id;
        if (this.batch.design_id) this.designId = this.batch.design_id;
        if (status === "paid") {
          // Submitted - the review page shows the status timeline.
          this.$router.replace(this.reviewLink);
          return;
        }
        if (status === "failed") {
          this.error = "Your payment did not complete. You can try buying this campaign again.";
          await this.loadAll();
          return;
        }
        // Still pending - reload the checkout and let the shopper retry.
        this.error = "We have not received confirmation of your payment yet. If you completed it, please refresh in a moment.";
        await this.loadAll();
      } catch (e) {
        this.error = this.errText(e, "We could not confirm your payment. Please try again shortly.");
        this.loading = false;
        this.confirming = false;
      }
    },
    openProof() {
      if (this.designId) window.open("/api/v1/direct-mail/designs/" + this.designId + "/proof.pdf", "_blank");
    },
    shortLabel(line) {
      if (line.key === "print") return "Print (" + this.fmt(this.quote.quantity) + " x " + this.formatLabel + ")";
      if (line.key === "postage") return "Postage";
      if (line.key === "data_fee") return "Data fee";
      return line.label;
    },
    money(pence) {
      return "£" + ((Number(pence) || 0) / 100).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    pence(p) {
      return Number(p) || 0;
    },
    fmt(n) {
      return (Number(n) || 0).toLocaleString("en-GB");
    },
    errText(e, fallback) {
      const body = e?.response?.data ?? e?.data;
      return body?.message || body?.error || fallback;
    },
  },
};
</script>

<style scoped>
.dm-page { max-width: 1100px; }
.text-muted { color: var(--m-text-secondary); }
.dm-head-row { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
.musubi-page__head h1 { font-size: 1.6rem; font-weight: 700; margin: 0 0 6px; display: flex; align-items: center; gap: 10px; }
.musubi-page__head p { margin: 0; max-width: 880px; line-height: 1.55; font-size: 0.92rem; }
.dm-badge { font-size: 0.6rem; font-weight: 800; letter-spacing: 0.05em; text-transform: uppercase; color: #fff; background: var(--m-accent, #4F46E5); border-radius: 999px; padding: 3px 8px; }
.plan-pill { display: inline-flex; align-items: center; gap: 5px; font-size: 0.6rem; font-weight: 800; letter-spacing: 0.05em; text-transform: uppercase; border-radius: 999px; padding: 3px 9px; }
.plan-pill--free { color: #6D28D9; background: #F3EEFE; border: 1px solid #E0D2FB; }

/* Stepper */
.dm-steps { display: flex; gap: 10px; margin: 18px 0 20px; flex-wrap: wrap; }
.dm-step { display: flex; align-items: center; gap: 10px; padding: 10px 16px; border: 1px solid var(--m-border); border-radius: 12px; background: var(--m-bg-card); flex: 1; min-width: 200px; text-decoration: none; }
.dm-step.is-current { border-color: var(--m-accent); box-shadow: 0 0 0 1px var(--m-accent) inset; }
.dm-step__num { width: 26px; height: 26px; border-radius: 50%; background: var(--m-accent-soft, #EEF0FF); color: var(--m-accent-hover, #4338CA); font-weight: 700; font-size: 0.82rem; display: flex; align-items: center; justify-content: center; flex: none; }
.dm-step.is-current .dm-step__num { background: var(--m-accent); color: #fff; }
.dm-step.is-done .dm-step__num { background: #DCF2ED; color: #0B6E63; }
.dm-step__title { display: block; font-weight: 600; font-size: 0.86rem; color: var(--m-text-primary); }
.dm-step__sub { display: block; font-size: 0.72rem; color: var(--m-text-tertiary); }

/* Summary cards */
.sum-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px; }
@media (max-width: 860px) { .sum-grid { grid-template-columns: 1fr; } }
.sum-card { background: var(--m-bg-card); border: 1px solid var(--m-border); border-radius: 14px; box-shadow: var(--m-shadow-card); padding: 16px; }
.sum-card__lbl { display: flex; align-items: center; gap: 6px; font-size: 0.72rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: var(--m-text-tertiary); }
.sum-card__lbl > svg { color: var(--m-accent); }
.sum-card__big { font-size: 1.7rem; font-weight: 800; color: var(--m-text-primary); margin-top: 8px; line-height: 1.1; }
.sum-card__sub { font-size: 0.76rem; color: var(--m-text-secondary); margin-bottom: 10px; }
.sum-list { list-style: none; margin: 0; padding: 10px 0 0; border-top: 1px dashed var(--m-border); }
.sum-list li { display: flex; justify-content: space-between; font-size: 0.8rem; padding: 4px 0; color: var(--m-text-secondary); }
.sum-list li b { color: var(--m-text-primary); }
.proof-thumb { display: flex; flex-direction: column; align-items: flex-start; gap: 8px; margin-top: 8px; }
.proof-thumb__vis { width: 100%; height: 78px; border: 1px dashed var(--m-border); border-radius: 8px; background: linear-gradient(180deg, #FBFCFE, var(--m-bg-card)); display: flex; align-items: center; justify-content: center; color: var(--m-accent); }
.proof-thumb__note { font-size: 0.72rem; color: var(--m-text-tertiary); }

/* Cards */
.dm-card { background: var(--m-bg-card); border: 1px solid var(--m-border); border-radius: 14px; box-shadow: var(--m-shadow-card); margin-bottom: 18px; }
.dm-card__head { display: flex; align-items: center; gap: 9px; padding: 14px 16px; border-bottom: 1px solid var(--m-border); }
.dm-card__icon { color: var(--m-accent); }
.dm-card__title { font-size: 0.92rem; font-weight: 700; margin: 0; }
.dm-card__subtitle { font-size: 0.76rem; color: var(--m-text-tertiary); }
.dm-card__body { padding: 16px; }

/* Cost table */
.cost { width: 100%; border-collapse: collapse; font-size: 0.84rem; }
.cost th, .cost td { text-align: left; padding: 8px 10px; border-bottom: 1px solid var(--m-border); }
.cost th { font-size: 0.72rem; text-transform: uppercase; letter-spacing: 0.04em; color: var(--m-text-tertiary); font-weight: 700; }
.cost .num { text-align: right; }
.cost tfoot td { font-weight: 600; color: var(--m-text-secondary); border-bottom: none; padding-top: 10px; }
.cost tfoot tr.grand td { font-weight: 800; color: var(--m-text-primary); font-size: 0.95rem; }
.cost-row--data td { background: #FAF7FF; color: #5B21B6; }
.cost-row--data td:first-child { border-left: 3px solid #7C3AED; }
.data-tag { display: inline-flex; align-items: center; gap: 4px; margin-left: 6px; font-size: 0.64rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.03em; color: #6D28D9; background: #F3EEFE; border: 1px solid #E0D2FB; border-radius: 999px; padding: 1px 7px; }
.data-tag > svg { width: 11px; height: 11px; }
.cost-note { display: flex; align-items: flex-start; gap: 8px; font-size: 0.76rem; color: var(--m-text-tertiary); margin-top: 12px; }
.cost-note > svg { flex: none; margin-top: 1px; }

/* Savings card */
.save-card { display: flex; gap: 16px; align-items: center; flex-wrap: wrap; background: #F7F3FE; border: 1px solid #E0D2FB; border-radius: 14px; padding: 16px 18px; margin-bottom: 18px; }
.save-card__ic { width: 42px; height: 42px; border-radius: 50%; background: #EDE3FE; color: #7C3AED; display: inline-flex; align-items: center; justify-content: center; flex: none; }
.save-card__body { flex: 1; min-width: 260px; }
.save-card__k { font-size: 0.68rem; font-weight: 800; text-transform: uppercase; letter-spacing: 0.05em; color: #7C3AED; }
.save-card__h { font-size: 1rem; font-weight: 700; color: #4C1D95; margin: 2px 0 4px; }
.save-card__h b { color: #4C1D95; }
.save-card__p { margin: 0; font-size: 0.82rem; line-height: 1.5; color: #5B21B6; }

/* Payment panel */
.pay-panel { display: grid; grid-template-columns: 1.4fr 1fr; gap: 16px; }
@media (max-width: 780px) { .pay-panel { grid-template-columns: 1fr; } }
.hermes-box { border: 1px solid var(--m-border); border-radius: 12px; padding: 14px 16px; background: linear-gradient(180deg, #FBFCFE, var(--m-bg-card)); }
.hermes-wm { display: flex; align-items: center; gap: 8px; font-weight: 800; color: var(--m-text-primary); }
.hermes-wm__mark { width: 26px; height: 26px; border-radius: 7px; background: var(--m-accent); color: #fff; display: inline-flex; align-items: center; justify-content: center; }
.hermes-wm .tag { font-size: 0.6rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: var(--m-text-tertiary); border: 1px solid var(--m-border); border-radius: 999px; padding: 1px 7px; }
.hermes-box__note { font-size: 0.8rem; line-height: 1.5; color: var(--m-text-secondary); margin: 10px 0 12px; }
.hermes-box__row { display: flex; align-items: center; gap: 8px; font-size: 0.82rem; color: var(--m-text-primary); padding: 3px 0; }
.hermes-box__row > svg { color: #0B6E63; flex: none; }
.order-sum { border: 1px solid var(--m-border); border-radius: 12px; padding: 14px 16px; background: var(--m-bg-card); align-self: start; }
.order-sum__h { font-size: 0.72rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; color: var(--m-text-tertiary); margin-bottom: 8px; }
.order-sum__line { display: flex; justify-content: space-between; font-size: 0.82rem; padding: 4px 0; color: var(--m-text-secondary); }
.order-sum__line b { color: var(--m-text-primary); }
.order-sum__total { display: flex; justify-content: space-between; align-items: baseline; margin-top: 8px; padding-top: 10px; border-top: 1px dashed var(--m-border); }
.order-sum__total span:first-child { font-weight: 700; color: var(--m-text-primary); }
.order-sum__total .num { font-size: 1.15rem; font-weight: 800; color: var(--m-text-primary); }

/* Action bar */
.dm-action-bar { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
.dm-action-bar__spacer { flex: 1; }
.pay-terms { display: flex; align-items: flex-start; gap: 8px; font-size: 0.76rem; color: var(--m-text-tertiary); margin: 0; max-width: 620px; line-height: 1.5; }
.pay-terms > svg { flex: none; margin-top: 1px; }
.pay-terms b { color: var(--m-text-secondary); }

/* Buttons */
.btn { font-family: inherit; font-weight: 600; font-size: 0.82rem; padding: 0.5rem 0.95rem; border-radius: 8px; border: 1px solid transparent; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; text-decoration: none; line-height: 1.3; white-space: nowrap; }
.btn:disabled { opacity: 0.55; cursor: not-allowed; }
.btn-sm { font-size: 0.76rem; padding: 0.35rem 0.7rem; }
.btn-lg { font-size: 0.9rem; padding: 0.6rem 1.2rem; }
.btn-pri { background: var(--m-accent); border-color: var(--m-accent); color: #fff; }
.btn-pri:hover:not(:disabled) { background: var(--m-accent-hover); }
.btn-sec { color: var(--m-text-secondary); border-color: var(--m-border); background: var(--m-bg-card); }
.btn-sec:hover:not(:disabled) { background: var(--m-accent-soft); border-color: var(--m-accent); color: var(--m-text-primary); }
.btn-violet { background: #7C3AED; border-color: #7C3AED; color: #fff; }
.btn-violet:hover { background: #6D28D9; }

/* Alerts */
.dm-alert { padding: 9px 12px; font-size: 0.8rem; border-radius: 8px; }
.dm-alert--err { background: #FCECEC; color: #B42318; border: 1px solid #F5C9C4; }
.dm-alert--warn { background: #FEF6E7; color: #92600A; border: 1px solid #F5E1B8; }
</style>
